import { FC, useState, useEffect, useMemo } from 'react';
import {
  Button,
  Modal,
  Radio,
  RadioChangeEvent,
  Space,
  Typography,
  message,
  Tooltip,
  Input,
  Skeleton,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { polarisApiGet, polarisApiPost } from 'packages/polaris/portal/rest';
import {
  dataTypeResponseArrayZod,
  triggerRequestCreateZod,
  triggerRequestResponseZod,
  DataTypeResponse, // Import DataTypeResponse
} from 'packages/polaris/portal/contract';
import { useUserProfile } from 'packages/lib/utils/hooks/use-auth-data';
import { selectedResultsAtom } from 'packages/omnitag/state';

const { Text } = Typography;

const timeIntervalOptions = [
  { label: '3 seconds', value: '3' },
  { label: '5 seconds', value: '5' },
  { label: '7 seconds', value: '7' },
  { label: '20 seconds', value: '20' },
];

interface DataRequestDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DataRequestDialog: FC<DataRequestDialogProps> = ({
  open,
  onClose,
}) => {
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState<string | undefined>(
    undefined
  );
  const [description, setDescription] = useState('');
  const [timeInterval, setTimeInterval] = useState(3);
  const { userProfile } = useUserProfile();
  const [downloadOptions, setDownloadOptions] = useState<
    Array<{ label: JSX.Element; value: string }>
  >([]);

  const selectedResults = useAtomValue(selectedResultsAtom);
  const setSelectedResults = useSetAtom(selectedResultsAtom);

  const samples = useMemo(() => Array.from(selectedResults), [selectedResults]);

  useEffect(() => {
    if (open) {
      setLoadingOptions(true);
      polarisApiGet<DataTypeResponse[]>(
        '/datatypes',
        dataTypeResponseArrayZod.parse
      )
        .then((options) => {
          if (options) {
            const formattedOptions = options.map((o) => ({
              label: (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 0',
                  }}
                >
                  <Tooltip title={o.description} placement="right">
                    <Typography.Text strong>{o.name}</Typography.Text>
                    <InfoCircleOutlined style={{ marginLeft: 8 }} />
                  </Tooltip>
                </div>
              ),
              value: o.name,
            }));
            setDownloadOptions(formattedOptions);
            // Pre-select the first option by default
            if (formattedOptions.length > 0) {
              setSelectedDataType(formattedOptions[0].value);
            }
          } else {
            setDownloadOptions([]); // Set to empty array if options is null
            setSelectedDataType(undefined);
          }
        })
        .finally(() => {
          setLoadingOptions(false);
        });
    } else {
      // Reset state when dialog is closed
      setSelectedDataType(undefined);
      setDescription('');
      setTimeInterval(3);
    }
  }, [open]);

  const handleTimeIntervalChange = (e: RadioChangeEvent) => {
    setTimeInterval(parseInt(e.target.value, 10));
  };

  const handleDataTypeChange = (e: RadioChangeEvent) => {
    setSelectedDataType(e.target.value);
  };

  const handleSubmit = async () => {
    if (!userProfile?.email || !selectedDataType || !description.trim()) {
      void message.error(
        'User email, data type, and description are required. Cannot submit request.'
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const processes = samples.flatMap((sample: OmnitagResultItem) => [
        {
          log_name: sample.log_name,
          datatype: selectedDataType,
          // Omnitag timestamp is in microseconds, API expects microseconds for start/end time
          start_time: Math.floor(sample.timestamp - (timeInterval * 1e6) / 2),
          end_time: Math.floor(sample.timestamp + (timeInterval * 1e6) / 2),
        },
      ]);

      const payload = triggerRequestCreateZod.parse({
        producer: 'omnitag', // Or derive this from somewhere if available
        user_email: userProfile.email,
        description: description.trim(),
        processes,
      });

      const response = await polarisApiPost(
        '/requests',
        payload,
        triggerRequestResponseZod.parse,
        true
      );

      if (response) {
        void message.success(
          `Data request submitted successfully! (ID: ${response.request_id})`
        );
        Modal.confirm({
          title: 'Data Request Submitted',
          content:
            'Do you want to reset your current data selection before returning to the search tool?',
          okText: 'Yes',
          cancelText: 'No',
          onOk() {
            setSelectedResults(new Set());
            onClose();
          },
          onCancel() {
            onClose();
          },
        });
      } else {
        void message.error('Failed to submit data request.');
        onClose(); // Close the modal even if submission fails
      }
    } catch (error) {
      console.error('Data request submission failed:', error);
      void message.error('An error occurred while submitting the request.');
      onClose(); // Close the modal if an error occurs
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Create New Data Request"
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="back" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={handleSubmit}
          disabled={!selectedDataType || !description.trim()}
        >
          <Tooltip
            title="Submit your request for data extraction"
            placement="bottom"
          >
            Request Data
          </Tooltip>
        </Button>,
      ]}
    >
      {loadingOptions ? (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Name / Description</Text>
            <Input.TextArea
              rows={2}
              placeholder="Loading description field..."
              disabled
              style={{ marginTop: '0.5rem' }}
            />
          </div>
          <div>
            <Text strong>Data Type</Text>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Skeleton.Input style={{ width: 200 }} active />
              <Skeleton.Input style={{ width: 200 }} active />
              <Skeleton.Input style={{ width: 200 }} active />
            </Space>
          </div>
          <div>
            <Text strong>Scene Duration in Seconds</Text>
            <Space>
              <Skeleton.Button active />
              <Skeleton.Button active />
              <Skeleton.Button active />
            </Space>
          </div>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Tooltip
              title="Provide a name or description for your data request"
              placement="left"
            >
              <Text strong>Name / Description</Text>
            </Tooltip>
            <Input.TextArea
              rows={2}
              placeholder="e.g. Data for fixing false positive pedestrian detections"
              maxLength={255}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ marginTop: '0.5rem' }}
              showCount
            />
          </div>
          <div>
            <Tooltip
              title="Select the type of data to include in your request"
              placement="left"
            >
              <Text strong>Data Type</Text>
            </Tooltip>
            <Radio.Group
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '0.5rem',
              }}
              options={downloadOptions}
              value={selectedDataType}
              onChange={handleDataTypeChange}
            />
          </div>

          <div>
            <Tooltip
              title="Choose the duration of the scene to extract around each selected image's timestamp"
              placement="left"
            >
              <Text strong>Scene Duration in Seconds</Text>
            </Tooltip>
            <Radio.Group
              style={{ display: 'block', marginTop: '0.5rem' }}
              options={timeIntervalOptions}
              defaultValue="3"
              optionType="button"
              buttonStyle="solid"
              onChange={handleTimeIntervalChange}
            />
            {timeInterval !== 0 && (
              <>
                <Text
                  type="secondary"
                  style={{
                    fontSize: '8pt',
                    display: 'block',
                    marginTop: '0.5rem',
                  }}
                >
                  Each scene will be centered around the image's timestamp. For
                  example, with a 3 second duration, a scene from 1.5 seconds
                  before to 1.5 seconds after the timestamp will be extracted.
                </Text>
              </>
            )}
          </div>
        </Space>
      )}
    </Modal>
  );
};
